import React, { useMemo, useEffect } from 'react';
import { useNode } from '@craftjs/core';
import { Resizer } from '../Resizer';
import { SignatureSettings } from './SignatureSettings';
import { usePropagateDimensions } from '../../../hooks/usePropagateDimensions';

const DEFAULT_WIDTH = '240px';
const DEFAULT_HEIGHT = '120px';
const ASPECT_RATIO_IMG = 400 / 150;

const defaultProps = {
    width: DEFAULT_WIDTH,
    height: DEFAULT_HEIGHT,
    margin: ['0', '0', '0', '0'],
    alignSelf: 'flex-end',
    signatureText: 'FIRMA',
    backgroundColor: { r: 255, g: 255, b: 255, a: 0 },
};

export const Signature = (props) => {
    const finalProps = { ...defaultProps, ...props };
    const { width, height, margin, alignSelf, signatureText, backgroundColor } = finalProps;

    const { propagateHeight, propagateWidth } = usePropagateDimensions();

    const {
        connectors: { connect },
        inNodeContext,
        dom,
        parent,
    } = useNode((node) => ({
        inNodeContext: node.inNodeContext,
        dom: node.dom,
        parent: node.data.parent,
    }));

    const maxWidth = useMemo(() => {
        if (!inNodeContext || !parent || !dom) return undefined;
        const parentEl = dom.parentElement;
        return parentEl ? parentEl.clientWidth : undefined;
    }, [inNodeContext, dom, parent]);

    useEffect(() => {
        if (inNodeContext && parent) {
            propagateWidth(parent);
            propagateHeight(parent);
        }
    }, [width, height, inNodeContext, parent, propagateWidth, propagateHeight]);

    const computedWidth = inNodeContext ? width : '100%';
    const computedHeight = inNodeContext ? height : 'auto';

    const containerStyles = {
        margin: `${margin[0]}px ${margin[1]}px ${margin[2]}px ${margin[3]}px`,
        width: computedWidth,
        height: computedHeight,
        alignSelf,
        flexShrink: 0,
        borderTop: '2px dashed #ccc',
        borderBottom: '2px dashed #ccc',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingTop: '5px',
        paddingBottom: '5px',
        boxSizing: 'border-box',
        backgroundColor: `rgba(${Object.values(backgroundColor)})`,
        maxWidth: maxWidth ? `${maxWidth}px` : undefined,
    };

    const innerStyles = {
        flexGrow: 1,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
    };

    const areaLabelStyles = {
        position: 'absolute',
        top: '30%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: '10px',
        color: '#999',
        fontWeight: 'bold',
        textAlign: 'center',
        lineHeight: 1.2,
        pointerEvents: 'none',
    };

    const signatureLineStyles = {
        width: '90%',
        height: '1px',
        backgroundColor: '#aaa',
        position: 'absolute',
        bottom: '5px',
        left: '5%',
    };

    const textLabelStyles = {
        fontSize: '10px',
        color: '#333',
        marginBottom: '5px',
        fontWeight: 600,
    };

    return (
        <Resizer
            key={`${width}-${height}`}
            ref={connect}
            propKey={inNodeContext ? { width: 'width', height: 'height' } : undefined}
            maxWidth={maxWidth ? `${maxWidth}px` : undefined}
            style={containerStyles}
        >
            <div style={innerStyles}>
                {finalProps.signatureUrl && (
                    <img 
                        src={finalProps.signatureUrl} 
                        alt="Firma digital" 
                        className="signature-image-element"
                        style={{ 
                            maxWidth: '90%', 
                            maxHeight: '90%', 
                            objectFit: 'contain',
                            position: 'absolute',
                            bottom: '15px',
                        }} 
                    />
                )}
                <div style={signatureLineStyles}></div> 
            </div>
            <span style={textLabelStyles}>{signatureText}</span>
        </Resizer>
    );
};

Signature.craft = {
    displayName: 'Firma',
    props: defaultProps,
    rules: { canDrag: () => true },
    related: { toolbar: SignatureSettings },
    custom: {
        toHtml: (props) => {
            const widthPx = props.width ? parseInt(props.width.replace('px', ''), 10) : 240;
            const imageWidthPx = Math.min(widthPx, 300);
            const [marginTop = '10', , marginBottom = '10'] = props.margin || [];

            const alignSelf = props.alignSelf || 'flex-end';
            const textAlignMap = {
                'flex-start': 'left',
                'center': 'center',
                'flex-end': 'right',
            };
            const textAlign = textAlignMap[alignSelf] || 'right';

            const containerStyle = `
                width:${props.width || '240px'};
                margin-top:${marginTop}px;
                margin-bottom:${marginBottom}px;
                display:flex;
                flex-direction:column;
                align-items:${alignSelf};
            `;

            return (signatureUrl) => {
                if (!signatureUrl) return '';
                const finalHeight = `${imageWidthPx / ASPECT_RATIO_IMG}px`;
                const text = props.signatureText || 'FIRMA';

                return `
                    <div style="${containerStyle}">
                        <img src="${signatureUrl}" alt="Firma" style="width:${imageWidthPx}px;height:${finalHeight};display:block;"/>
                        <hr style="width:${imageWidthPx}px;border:0;border-top:1px solid #000;margin:2px 0;border-width:0.5px;"/>
                        <p style="text-align:center;width:${imageWidthPx}px;margin:0;font-size:10px;">${text}</p>
                    </div>
                `;
            };
        },
    },
};
